import React from 'react'
import {Button, Col, Row, TimePicker} from 'antd'
import {Controller, useFieldArray, useFormContext} from 'react-hook-form'
import dayjs from 'dayjs'
import {DeleteOutlined, MinusOutlined, PlusOutlined} from '@ant-design/icons'

interface HourPickerFieldProps {
  label: string
  name: string
  required?: boolean
  disabled?: boolean | [boolean, boolean]
  top?: boolean
}

const timeFormat = 'HH:mm'

const addHours = (time: string, hours: number) => {
  return dayjs(time, timeFormat).add(hours, 'hour').format(timeFormat)
}

const getNextTimeRange = (timeTo: string) => {
  return {timeFrom: addHours(timeTo, 1), timeTo: addHours(timeTo, 2)}
}

const HourPickerField: React.FC<HourPickerFieldProps> = ({
  label,
  name,
  required,
  disabled,
  top,
}) => {
  const form = useFormContext()
  const {fields, append, remove} = useFieldArray({name})

  const value = form.watch(name)

  const checked = !!value && value.length > 0

  const handleChange = () => {
    if (checked) {
      form.setValue(name, [], {shouldDirty: true})
    }
  }

  return (
    <Row>
      <Col
        span={8}
        className={`py-5${
          top ? 'pt-0' : 'sm:border-t'
        } sm:border-gray-200`}
      >
        <label className='flex items-center text-sm font-medium text-gray-700 pt-2'>
          <Button
            type='link'
            icon={<DeleteOutlined />}
            onClick={handleChange}
            danger
            disabled={!checked}
          />
          {label}
        </label>
      </Col>
      <Col span={16}>
        {fields.map((item, index) => (
          <Row className='mb-3' key={item.id}>
            <Controller
              control={form.control}
              name={`${name}.${index}`}
              render={({field: {onChange, value}}) => (
                <TimePicker.RangePicker
                  className='max-w-lg w-[400px] shadow-sm sm:max-w-xs h-[38px] border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 rounded-md'
                  allowClear={false}
                  format={timeFormat}
                  disabled={disabled}
                  minuteStep={15}
                  onChange={(range) => {
                    if (range?.[0] && range?.[1]) {
                      onChange({
                        timeFrom: range[0].format(timeFormat),
                        timeTo: range[1].format(timeFormat),
                      })
                    } else {
                      onChange(null) // Reset if range is invalid or cleared
                    }
                  }}
                  value={
                    value && [
                      dayjs(value.timeFrom, timeFormat),
                      dayjs(value.timeTo, timeFormat),
                    ]
                  }
                />
              )}
            />
            <Button className='ml-3 h-[38px] !w-[38px]' icon={<MinusOutlined />} onClick={() => remove(index)} />
          </Row>
        ))}
        <Button
          className='!w-[320px] h-[38px]'
          icon={<PlusOutlined />}
          onClick={() =>
            append(getNextTimeRange((fields[fields.length - 1] as any)?.timeTo || '07:00'))
          }
        >Add hour targeting</Button>
      </Col>
    </Row>
  )
}

export default HourPickerField
